/**
 * Paeru Exchange Trading Dashboard - Main App Component
 * "Paeru" is Aramaic for "Money"
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import AllStocks from './pages/AllStocks';
import Portfolio from './pages/Portfolio';
import Gurus from './pages/Gurus';
import Options from './pages/Options';
import Crypto from './pages/Crypto';
import NewsDashboard from './pages/NewsDashboard';
import PlatformAdmin from './pages/PlatformAdmin';
import Leaderboard from './pages/Leaderboard';
import PositionManager from './pages/PositionManager';
import StrategyTemplates from './pages/StrategyTemplates';
import TradeJournal from './pages/TradeJournal';
import Backtest from './pages/Backtest';
import Performance from './pages/Performance';
import StockScanner from './pages/StockScanner';
import Scorecard from "./pages/Scorecard";
import AgentTeam from './pages/AgentTeam';
import WarRoom from './pages/WarRoom';
import Help from './pages/Help';
import AppShell from './components/AppShell';

// Protected Route wrapper. `perm` gates page access by role (RBAC); a user who lacks
// it sees an in-shell "no access" panel instead of the page (URL-typing can't bypass nav gating).
const ProtectedRoute = ({ children, perm }) => {
  const { isAuthenticated, loading, can } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-border-color border-t-accent-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (perm && !can(perm)) {
    return (
      <AppShell>
        <div className="max-w-md mx-auto mt-24 text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold mb-2">Access restricted</h2>
          <p className="text-text-secondary">Your role doesn't have access to this page. Contact an administrator if you need it.</p>
        </div>
      </AppShell>
    );
  }

  return <AppShell>{children}</AppShell>;
};

// Public Route wrapper (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-3 border-border-color border-t-accent-blue rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute perm="nav.dashboard">
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute perm="nav.settings">
            <Settings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute perm="nav.botcontrol">
            <Admin />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/stocks" 
        element={
          <ProtectedRoute perm="nav.stocks">
            <AllStocks />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portfolio" 
        element={
          <ProtectedRoute perm="nav.portfolio">
            <Portfolio />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/gurus" 
        element={
          <ProtectedRoute perm="nav.gurus">
            <Gurus />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/options" 
        element={
          <ProtectedRoute perm="nav.options">
            <Options />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/crypto" 
        element={
          <ProtectedRoute perm="nav.crypto">
            <Crypto />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/news" 
        element={
          <ProtectedRoute perm="nav.news">
            <NewsDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/platform-admin" 
        element={
          <ProtectedRoute perm="nav.platformadmin">
            <PlatformAdmin />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/leaderboard" 
        element={
          <ProtectedRoute perm="nav.leaderboard">
            <Leaderboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/positions" 
        element={
          <ProtectedRoute perm="nav.positions">
            <PositionManager />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/strategies" 
        element={
          <ProtectedRoute perm="nav.strategies">
            <StrategyTemplates />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/journal" 
        element={
          <ProtectedRoute perm="nav.journal">
            <TradeJournal />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/backtest" 
        element={
          <ProtectedRoute perm="nav.backtest">
            <Backtest />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/performance" 
        element={
          <ProtectedRoute perm="nav.performance">
            <Performance />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scanner" 
        element={
          <ProtectedRoute perm="nav.scanner">
            <StockScanner />
          </ProtectedRoute>
        } 
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/scorecard" element={<ProtectedRoute perm="nav.scorecard"><Scorecard /></ProtectedRoute>} />
      <Route path="/agents" element={<ProtectedRoute perm="nav.agents"><AgentTeam /></ProtectedRoute>} />
      <Route path="/war-room" element={<ProtectedRoute perm="nav.warroom"><WarRoom /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#16161f',
              color: '#ffffff',
              border: '1px solid #2a2a3a',
            },
            success: {
              iconTheme: {
                primary: '#00ff88',
                secondary: '#16161f',
              },
            },
            error: {
              iconTheme: {
                primary: '#ff3366',
                secondary: '#16161f',
              },
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
